import type { StorageHealth, StorageHealthPort } from "./ports.ts";

export class BrowserStorageHealthPort implements StorageHealthPort {
  async inspect(): Promise<StorageHealth> {
    const storage = navigator.storage;
    const persistenceRequestSupported = typeof storage.persist === "function";
    const [persisted, estimate]: [boolean, StorageEstimate] = await Promise.all([
      typeof storage.persisted === "function"
        ? storage.persisted()
        : Promise.resolve(false),
      typeof storage.estimate === "function"
        ? storage.estimate()
        : Promise.resolve({}),
    ]);
    return toHealth(
      persisted,
      persistenceRequestSupported,
      estimate.usage,
      estimate.quota,
    );
  }

  async requestPersistence(): Promise<StorageHealth> {
    if (typeof navigator.storage.persist !== "function") {
      return this.inspect();
    }
    await navigator.storage.persist();
    return this.inspect();
  }
}

interface StorageEstimate {
  usage?: number;
  quota?: number;
}

function toHealth(
  persisted: boolean,
  persistenceRequestSupported: boolean,
  usage: number | undefined,
  quota: number | undefined,
): StorageHealth {
  let pressure: StorageHealth["pressure"] = "unknown";
  if (usage !== undefined && quota !== undefined && quota > 0) {
    const ratio = usage / quota;
    pressure = ratio >= 0.9 ? "critical" : ratio >= 0.75 ? "warning" : "normal";
  }
  return {
    persisted,
    persistenceRequestSupported,
    pressure,
    ...(usage === undefined ? {} : { usage }),
    ...(quota === undefined ? {} : { quota }),
  };
}
